// ClientNameInput.jsx — the "Full Name" field for NEW clients, with live suggestions of
// existing clients whose name matches what's being typed (words in any order,
// case-insensitive), so duplicates are spotted before they're created.
// Clicking a suggestion calls onOpenExisting(client) — e.g. open that client instead.
import React, { useState, useEffect, useRef } from "react";
import { api } from "../api";
import { Inp } from "../ui";
import { G, GL } from "./constants";

// Escape regex special characters so typed text like "O'Brien (Jr)" can't break the search.
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Every typed word must appear somewhere in the name, in any order:
// "ikechukwu osili" → ^(?=.*ikechukwu)(?=.*osili)
const toPattern = (text) =>
  "^" + text.trim().split(/\s+/).filter(Boolean).map((w) => `(?=.*${esc(w)})`).join("");

export default function ClientNameInput({ value, onChange, onOpenExisting, label = "Full Name *" }) {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const typed = (value || "").trim();

  // Debounced lookup while typing (min 2 characters).
  useEffect(() => {
    if (typed.length < 2) { setMatches([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/crm/clients?page=1&limit=6&search=${encodeURIComponent(toPattern(typed))}`);
        const words = typed.toLowerCase().split(/\s+/);
        // Keep name matches only (the backend also searches email/phone).
        const list = (res.data || []).filter((c) =>
          words.every((w) => (c.fullName || "").toLowerCase().includes(w)));
        if (!cancelled) setMatches(list);
      } catch {
        if (!cancelled) setMatches([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [typed]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  // Bold the typed words inside the name.
  const highlight = (name) => {
    const words = typed.split(/\s+/).filter(Boolean).map(esc);
    if (!words.length) return name;
    const parts = String(name).split(new RegExp(`(${words.join("|")})`, "ig"));
    return parts.map((p, i) =>
      words.some((w) => new RegExp(`^${w}$`, "i").test(p))
        ? <strong key={i} className="text-slate-900">{p}</strong>
        : <span key={i}>{p}</span>);
  };

  const show = open && typed.length >= 2 && (loading || matches.length > 0);

  return (
    <div className="relative" ref={boxRef}>
      <Inp
        label={label}
        placeholder="Jane Doe"
        value={value}
        onChange={(e) => { onChange(e); setOpen(true); }}
        autoComplete="off"
        required
      />
      {show && (
        <div className="mt-1.5 border border-amber-200 rounded-xl overflow-hidden shadow-sm bg-white">
          <p className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-amber-700 bg-amber-50 border-b border-amber-100">
            {loading && !matches.length ? "Checking existing clients…" : "Existing clients with this name"}
          </p>
          {matches.map((c) => {
            const latest = c.trips?.[c.trips.length - 1];
            return (
              <button
                key={c._id}
                type="button"
                onClick={() => { setOpen(false); onOpenExisting?.(c); }}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
                title={onOpenExisting ? "Open this client instead" : undefined}
              >
                <span
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-black flex-shrink-0"
                  style={{ background: GL, color: G }}
                >
                  {(c.fullName || "?").charAt(0).toUpperCase()}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-xs text-slate-600 truncate">{highlight(c.fullName)}</span>
                  <span className="block text-[10px] text-slate-400 truncate">
                    {c.email}
                    {latest?.destinations?.length ? ` · ${latest.destinations.join(", ")}` : ""}
                    {c.trips?.length ? ` · ${c.trips.length} trip${c.trips.length !== 1 ? "s" : ""}` : ""}
                  </span>
                </span>
                {onOpenExisting && (
                  <span className="text-[10px] font-bold flex-shrink-0" style={{ color: G }}>Open →</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
